import React from 'react';
import PropTypes from 'prop-types';
import {
  Form,
  InputNumber,
  Radio,
  Switch,
  Select,
  Table,
  Tooltip,
  Typography
} from 'antd';
import { useTranslation } from 'react-i18next';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons';

import useQuotesContext from './QuotesContext';
import { fractionType } from '../../utils/constants/misc';
import {
  assoSwitchesDefaultRc,
  assoSwitchesDefaultLocal
} from './assoSwitchDefault';

const ComparisonTable = ({
  fields,
  defaultPackageValues,
  columns,
  form,
  assoType
}) => {
  const { formValues, setFormValues, estimatedCost, exemptions } =
    useQuotesContext();
  const { t } = useTranslation();
  const chosenPckg = formValues?.[formValues?.package];

  const handleChangeTable = (name, value, pckg) => {
    const changedValue = { [name]: value };
    const classic_accident_insurance = form.getFieldValue([
      pckg,
      'classic_accident_insurance'
    ]);
    if (!classic_accident_insurance) {
      form.setFieldsValue({
        [pckg]: {
          reinforced_accident_insurance: false
        }
      });
    }
    setFormValues({
      ...formValues,
      [pckg]: {
        ...form.getFieldValue([pckg], name.toString())
      },
      ...changedValue
    });
  };

  const handleRcProChange = (name, pckg) => {
    if (name[0] === 'rc_pro') {
      form.setFieldsValue({
        [pckg]: {
          rc_pro: form.getFieldValue([pckg, 'rc_pro']),
          social_mandate_responsibility: false
        }
      });
      setFormValues({
        ...formValues,
        [pckg]: {
          ...formValues[pckg],
          social_mandate_responsibility: false,
          rc_pro: form.getFieldValue([pckg, 'rc_pro'])
        },
        ...chosenPckg
      });
    }
  };

  const inputs = {
    ...(assoType && assoType === 'rc'
      ? {
          fixed: (name) =>
            assoSwitchesDefaultRc[name] ? (
              <CheckCircleOutlined />
            ) : (
              <CloseCircleOutlined />
            ),
          switch: (name, pckg) => (
            <Switch
              onChange={(value) =>
                handleChangeTable(name.toString(), value, pckg)
              }
            />
          ),
          number: (name, pckg) => (
            <InputNumber
              style={{ width: '100%' }}
              onChange={(value) =>
                handleChangeTable(name.toString(), value, pckg)
              }
            />
          )
        }
      : {
          fixed: (name) =>
            assoType === 'localRc' ? (
              assoSwitchesDefaultLocal[name] ? (
                <CheckCircleOutlined />
              ) : (
                <CloseCircleOutlined />
              )
            ) : defaultPackageValues.PACKAGE_1[name] ? (
              <CheckCircleOutlined />
            ) : (
              <CloseCircleOutlined />
            ),
          switch: (name, pckg) => (
            <Switch
              onChange={(value) =>
                assoType
                  ? handleChangeTable(name.toString(), value, pckg)
                  : handleRcProChange(name, pckg)
              }
            />
          ),
          number: (name, pckg) => (
            <InputNumber
              style={{ width: '100%' }}
              disabled={
                assoType === 'localRc' &&
                name.toString() !== 'fire_explosion_attack_climatic_event' &&
                name.toString() !== 'electric_damage'
              }
              min={
                assoType === 'localRc' &&
                name.toString() === 'fire_explosion_attack_climatic_event'
                  ? 10000
                  : 0
              }
              onChange={(value) =>
                handleChangeTable(name.toString(), value, pckg)
              }
            />
          ),
          select: (name, pckg) => (
            <Select
              defaultValue="5000"
              style={{ width: '100%' }}
              onChange={(value) =>
                handleChangeTable(name.toString(), value, pckg)
              }
              options={[
                { value: 5000, label: '5000' },
                { value: 10000, label: '10000' },
                { value: 15000, label: '15000' },
                { value: 20000, label: '20000' }
              ]}
            />
          )
        })
  };

  const handleChangePackage = (pckg) => {
    setFormValues({ ...formValues, package: pckg, ...chosenPckg });
    form.setFieldsValue({ package: pckg });
  };

  const costGivenFractionType = (n) =>
    Object.entries(fractionType).map(([key, coef]) => (
      <Typography.Paragraph key={key} style={{ marginBottom: 4 }}>
        {`${(n * coef).toFixed(2)}€/${t(`quotes.fraction_type.${key}`)}`}
      </Typography.Paragraph>
    ));

  const formattedColumns = columns.map((col) => {
    return {
      ...col,
      title: assoType ? (
        t(`contracts.form.options.${col.title}`)
      ) : (
        <Radio
          checked={formValues?.package === col.title}
          onChange={() => handleChangePackage(col.title)}
        >
          {t(`contracts.form.options.${col.title}`)}
        </Radio>
      ),
      align: 'center',
      width: 200,
      render: (input, row, index) => {
        if (['estimations1', 'estimations2'].includes(row.name)) {
          if (exemptions.length)
            return `Conditions & tarifs personnalisés sous étude en ligne par notre service (réponse ultra-rapide!)`;
          return costGivenFractionType(input[col.title]);
        }
        if (row.name === 'choices')
          return assoType ? (
            t(`contracts.form.options.${col.title}`)
          ) : (
            <Radio
              checked={
                assoType === 'rc' || assoType === 'localRc'
                  ? true
                  : formValues?.package === col.title
              }
              onChange={() => handleChangePackage(col.title)}
            >
              {t(`contracts.form.options.${col.title}`)}
            </Radio>
          );
        if (index < fields.length + 1)
          return (
            <Form.Item
              valuePropName={input?.type === 'number' ? undefined : 'checked'}
              name={[col.title, input.name]}
            >
              {inputs[input.type || 'switch'](input.name, col.title)}
            </Form.Item>
          );
        return input;
      }
    };
  });

  const formatGuaranteeName = (data) => {
    const tooltipFields = [
      'legal_protection_mrp',
      'extended_legal_protection_mrp',
      'extended_legal_protection',
      'social_mandate_responsibility',
      'exploitation_responsibility',
      'local_insurance',
      'legal_protection',
      'civil_responsibility_asso',
      'classic_accident_insurance',
      'reinforced_accident_insurance',
      'light_inflatable_structure'
    ];

    const hasTooltip =
      tooltipFields.includes(data.name[0]) ||
      (data.name[0] === 'water_damage' && assoType === 'localRc') ||
      (data.name[0] === 'theft_vandalism' && assoType === 'localRc') ||
      (data.name[0] === 'broken_windows' && assoType === 'localRc');

    return (
      <>
        {t(`contracts.form.${data.name}`)}
        {hasTooltip && (
          <Tooltip placement="top" title={t(`quotes.tooltip.${data.name}`)}>
            <QuestionCircleOutlined />
          </Tooltip>
        )}
      </>
    );
  };

  const firstColumn = {
    title: 'Nature des garanties',
    width: 300,
    fixed: 'left',
    render: (input) => formatGuaranteeName(input)
  };

  const adjustedFields = [
    { ...estimatedCost, name: 'estimations1' },
    ...fields,
    { ...estimatedCost, name: 'estimations2' },
    { name: 'choices' }
  ];

  return (
    <div className="comparison-table">
      <Table
        dataSource={adjustedFields}
        columns={[firstColumn, ...formattedColumns]}
        rowKey="name"
        pagination={false}
        scroll={{ x: 'max-content', y: 550 }}
        rowClassName={(record) =>
          record.name === 'estimations1' || record.name === 'estimations2'
            ? 'estimation-row'
            : ''
        }
      />
    </div>
  );
};

ComparisonTable.propTypes = {
  fields: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  defaultPackageValues: PropTypes.shape({ PACKAGE_1: PropTypes.shape({}) }),
  form: PropTypes.shape({
    setFieldsValue: PropTypes.func,
    getFieldValue: PropTypes.func
  }).isRequired,
  assoType: PropTypes.string
};

ComparisonTable.defaultProps = {
  defaultPackageValues: {},
  assoType: null
};

export default ComparisonTable;
